import { Link } from 'react-router-dom';
import { Phone, Envelope, MapPin, GoogleLogo, Clock, FacebookLogo, Globe } from '@phosphor-icons/react';
import { BRAND, COVERAGE } from '../lib/brand';
import { SERVICES_INDEX } from '../lib/servicesIndex';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const navLinks = [
    { to: '/', label: 'Home', testid: 'footer-link-home' },
    { to: '/about', label: 'About India', testid: 'footer-link-about' },
    { to: '/services', label: 'All Services', testid: 'footer-link-services' },
    { to: '/areas-we-serve', label: 'Areas We Serve', testid: 'footer-link-areas' },
    { to: '/booking', label: 'Book an Appointment', testid: 'footer-link-booking' },
  ];

  // Popular city links — shown as a horizontal strip above the copyright bar
  const popularCities = [
    { slug: 'nashville', name: 'Nashville' },
    { slug: 'franklin', name: 'Franklin' },
    { slug: 'brentwood', name: 'Brentwood' },
    { slug: 'hendersonville', name: 'Hendersonville' },
    { slug: 'murfreesboro', name: 'Murfreesboro' },
    { slug: 'mt-juliet', name: 'Mt. Juliet' },
    { slug: 'gallatin', name: 'Gallatin' },
    { slug: 'lebanon', name: 'Lebanon' },
    { slug: 'smyrna', name: 'Smyrna' },
    { slug: 'spring-hill', name: 'Spring Hill' },
  ];

  return (
    <footer data-testid="main-footer" className="bg-[#4A0000] text-white relative overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="inline-block bg-white p-1.5 rounded-full shadow-lg mb-5">
              <img
                src={BRAND.footerLogoUrl}
                alt="India's Inking Service"
                className="h-24 w-24 rounded-full object-cover"
              />
            </div>
            <p className="font-serif-display italic text-[#F5E6A8] text-lg mb-3">
              "Safe. Genuine. Legally binding."
            </p>
            <p className="text-white/70 text-sm leading-relaxed mb-5">
              MBA-led mobile notary in Nashville, a nationwide signing-agent network, and Tennessee-compliant Remote Online Notarization for clients anywhere in the world — 24 / 7.
            </p>

            {/* Social */}
            <div className="flex items-center gap-3">
              <a
                href={BRAND.facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="India's Inking Service on Facebook"
                data-testid="footer-facebook-link"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#D4AF37] hover:text-[#1A0000] text-white flex items-center justify-center transition-all duration-300"
              >
                <FacebookLogo weight="fill" size={20} />
              </a>
              <a
                href={BRAND.googleReviewsUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Google reviews"
                data-testid="footer-google-icon-link"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#D4AF37] hover:text-[#1A0000] text-white flex items-center justify-center transition-all duration-300"
              >
                <GoogleLogo weight="fill" size={20} />
              </a>
            </div>
          </div>

          {/* Navigate — fully clickable */}
          <div>
            <h4 className="font-serif-display font-semibold text-lg mb-5 text-[#D4AF37]">Navigate</h4>
            <ul className="space-y-3 text-sm">
              {navLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={link.testid}
                    className="text-white/70 hover:text-[#D4AF37] hover:translate-x-0.5 inline-block transition-all"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services — every item now linked to its detail page */}
          <div>
            <h4 className="font-serif-display font-semibold text-lg mb-5 text-[#D4AF37]">Services</h4>
            <ul className="space-y-3 text-sm">
              {SERVICES_INDEX.map((service) => (
                <li key={service.slug}>
                  <Link
                    to={`/services/${service.slug}`}
                    data-testid={`footer-service-link-${service.slug}`}
                    className="text-white/70 hover:text-[#D4AF37] hover:translate-x-0.5 inline-block transition-all"
                  >
                    {service.short}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact + 3-tier coverage */}
          <div>
            <h4 className="font-serif-display font-semibold text-lg mb-5 text-[#D4AF37]">Reach India</h4>
            <ul className="space-y-4">
              <li>
                <a href={`tel:${BRAND.phoneRaw}`} data-testid="footer-phone" className="flex items-center gap-3 text-white/85 hover:text-[#D4AF37] transition-colors">
                  <Phone weight="fill" size={18} className="text-[#D4AF37]" />
                  <span>{BRAND.phone}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${BRAND.email}`} data-testid="footer-email" className="flex items-center gap-3 text-white/85 hover:text-[#D4AF37] transition-colors break-all">
                  <Envelope weight="fill" size={18} className="text-[#D4AF37] flex-shrink-0" />
                  <span>{BRAND.email}</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-white/70 text-sm">
                <MapPin weight="fill" size={18} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span>Mobile in Nashville &amp; Middle Tennessee</span>
              </li>
              <li className="flex items-start gap-3 text-white/70 text-sm">
                <Globe weight="fill" size={18} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span>Nationwide signing network · RON worldwide</span>
              </li>
              <li className="flex items-start gap-3 text-white/70 text-sm">
                <Clock weight="fill" size={18} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span>Available 24 / 7</span>
              </li>
              <li>
                <a
                  href={BRAND.googleReviewsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-google-reviews"
                  className="flex items-center gap-3 text-white/85 hover:text-[#D4AF37] transition-colors text-sm"
                >
                  <GoogleLogo weight="fill" size={18} className="text-[#D4AF37]" />
                  <span>Read Google Reviews</span>
                </a>
              </li>
              <li>
                <a
                  href={BRAND.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-facebook-text-link"
                  className="flex items-center gap-3 text-white/85 hover:text-[#D4AF37] transition-colors text-sm"
                >
                  <FacebookLogo weight="fill" size={18} className="text-[#D4AF37]" />
                  <span>Follow on Facebook</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Popular cities strip */}
        <div className="mt-14 pt-8 border-t border-white/10">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] font-bold mb-4">
            Mobile Notary in
          </p>
          <div className="flex flex-wrap gap-2">
            {popularCities.map((c) => (
              <Link
                key={c.slug}
                to={`/areas/city/${c.slug}`}
                data-testid={`footer-city-link-${c.slug}`}
                className="text-xs text-white/70 hover:text-[#D4AF37] hover:bg-white/5 px-3 py-1.5 rounded-sm border border-white/10 hover:border-[#D4AF37]/40 transition-all"
              >
                {c.name}
              </Link>
            ))}
            <Link
              to="/areas-we-serve"
              data-testid="footer-areas-view-all"
              className="text-xs text-[#D4AF37] hover:text-[#F5E6A8] px-3 py-1.5 rounded-sm border border-[#D4AF37]/40 hover:border-[#D4AF37] transition-all font-semibold"
            >
              All areas →
            </Link>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-xs tracking-wide">
            © {currentYear} India's Inking Service · Nashville, Tennessee · All rights reserved.
          </p>
          <p className="text-white/50 text-xs italic font-serif-display text-center">
            Bonded &amp; Commissioned Notary Public · State of Tennessee
          </p>
        </div>
      </div>
    </footer>
  );
};
